import React, { useState, useEffect, useRef, useCallback } from "react";
import Header from "./Header.js";
import Footer from "./Footer.js";
import Select from "react-select";
import { TailSpin } from "react-loader-spinner";
import "bootstrap-icons/font/bootstrap-icons.css";
import "./Style.css";
import Update_PassPayout from "../../Components/Admin_components/update_pass_payout.js";
import { getAdminPassPayout } from "../../Api/apiUrls";
import BankDetailsPopup from "../../Components/Admin_components/bankDetailsPopup.js";

// Debounce function to limit rapid firing of events
function debounce(func, wait) {
  let timeout;
  return function(...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
}

function PassPayout() {
  // Data states
  const [allPayouts, setAllPayouts] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [pagination, setPagination] = useState({});
  
  // Loading states
  const [isLoading, setIsLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);

  // Modal and selection states
  const [modalUpdate_PassPayoutIsOpen, setUpdate_PassPayoutModalIsOpen] = useState(false);
  const [selectedpassPayoutId, setselectedpassPayoutId] = useState(null);
  const [selectedpassPayoutStatus, setselectedpassPayoutStatus] = useState(null);
  const [selectedinstructorPayment, setselectedinstructorPayment] = useState(null);
  const [selectedvenueProviderPayment, setselectedvenueProviderPayment] = useState(null);
  const [selectedbrandRepPayment, setselectedbrandRepPayment] = useState(null);
  const [selectedinstructorPaymentDone, setselectedinstructorPaymentDone] = useState(null);
  const [selectedvenueProviderPaymentDone, setselectedvenueProviderPaymentDone] = useState(null);
  const [selectedbrandRepPaymentDone, setselectedbrandRepPaymentDone] = useState(null);
  const [selectedremark, setselectedremark] = useState(null);
  
  // Bank details popup
  const [isBankDetailsPopupOpen, setIsBankDetailsPopupOpen] = useState(false);
  const [selectedBankDetails, setSelectedBankDetails] = useState({});
  
  // Filter and search states
  const [workshopSlotStatusFilter, setWorkshopSlotStatusFilter] = useState("all");
  const [payoutStatusFilter, setPayoutStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const tableContainerRef = useRef();
  const searchInputRef = useRef();

  const workshopSlotStatusOptions = [
    { value: "all", label: "All Workshop Status" },
    { value: "Available", label: "Available" },
    { value: "Reviewing", label: "Reviewing" },
    { value: "Complete", label: "Complete" },
  ];

  const payoutStatusOptions = [
    { value: "all", label: "All Payout Status" },
    { value: "Pending", label: "Pending" },
    { value: "Approved", label: "Approved" },
    { value: "Rejected", label: "Rejected" },
  ];

  const handleWorkshopSlotStatusChange = (selectedOption) => {
    setWorkshopSlotStatusFilter(selectedOption.value);
    setPage(1);
    setAllPayouts([]);
  };

  const handlePayoutStatusChange = (selectedOption) => {
    setPayoutStatusFilter(selectedOption.value);
    setPage(1);
    setAllPayouts([]);
  };

  const handleSearchInputChange = (event) => {
    const newValue = event.target.value;
    setSearchQuery(newValue);
    
    if (newValue === "") {
      setSearchTerm("");
      setPage(1);
      setAllPayouts([]);
      fetchPassPayout(1, true);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setSearchTerm(searchQuery);
    setPage(1);
    setAllPayouts([]);
    fetchPassPayout(1, true);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearchSubmit(e);
    }
  };

  const fetchPassPayout = useCallback(async (pageNum = 1, reset = false) => {
    // Don't fetch if we're trying to go beyond total pages
    if (pagination.total_pages && pageNum > pagination.total_pages) {
      setHasMore(false);
      return;
    }
    
    setIsFetching(true);
    try {
      let url = `${getAdminPassPayout}?page=${pageNum}&page_size=50`;
      
      if (workshopSlotStatusFilter !== "all") {
        url += `&workshop_slot_status=${workshopSlotStatusFilter}`;
      }
      
      if (payoutStatusFilter !== "all") {
        url += `&payout_status=${payoutStatusFilter}`;
      }
      
      if (searchTerm) {
        url += `&search=${encodeURIComponent(searchTerm)}`;
      }

      const response = await fetch(url);
      if (!response.ok) throw new Error("Network response was not ok");
      
      const data = await response.json();
      
      if (!data.data || !data.data.PassManagementScheduleBooked) {
        throw new Error("Invalid data format from API");
      }
      
      const newPayouts = data.data.PassManagementScheduleBooked;
      
      if (reset || pageNum === 1) {
        setAllPayouts(newPayouts);
      } else {
        setAllPayouts(prev => [...prev, ...newPayouts]);
      }
      
      setPagination(data.data.pagination || {});
      setTotalItems(data.data.pagination?.total_items || 0);
      
      const currentPage = data.data.pagination?.current_page || 1;
      const totalPages = data.data.pagination?.total_pages || 1;
      setHasMore(currentPage < totalPages);
    } catch (error) {
      console.error("Error fetching data:", error);
      setAllPayouts([]);
      setTotalItems(0);
    } finally {
      setIsFetching(false);
      setIsLoading(false);
    }
  }, [workshopSlotStatusFilter, payoutStatusFilter, searchTerm, pagination.total_pages]);

  useEffect(() => {
    fetchPassPayout(page);
  }, [page, fetchPassPayout]);

  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container || isFetching || !hasMore) return;

    const handleScroll = debounce(() => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (scrollHeight - (scrollTop + clientHeight) < 50) {
        setPage(prev => {
          const nextPage = prev + 1;
          // Check if nextPage exceeds total pages
          if (pagination.total_pages && nextPage > pagination.total_pages) {
            return prev;
          }
          return nextPage;
        });
      }
    }, 200);

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [isFetching, hasMore, pagination.total_pages]);

  const openUpdate_passPayout = (
    passPayoutId,
    passPayoutStatus,
    instructorPayment,
    venueProviderPayment,
    brandRepPayment,
    instructorPaymentDone,
    venueProviderPaymentDone,
    brandRepPaymentDone,
    remark
  ) => {
    setselectedpassPayoutId(passPayoutId);
    setselectedpassPayoutStatus(passPayoutStatus);
    setselectedinstructorPayment(instructorPayment);
    setselectedvenueProviderPayment(venueProviderPayment);
    setselectedbrandRepPayment(brandRepPayment);
    setselectedinstructorPaymentDone(instructorPaymentDone);
    setselectedvenueProviderPaymentDone(venueProviderPaymentDone);
    setselectedbrandRepPaymentDone(brandRepPaymentDone);
    setselectedremark(remark);
    setUpdate_PassPayoutModalIsOpen(true);
  };

  const closeUpdate_passPayout = () => {
    setUpdate_PassPayoutModalIsOpen(false);
    setPage(1);
    fetchPassPayout(1, true);
  };

  const openBankDetailsPopup = (bankDetails) => {
    setSelectedBankDetails(bankDetails || {});
    setIsBankDetailsPopupOpen(true);
  };

  const closeBankDetailsPopup = () => {
    setIsBankDetailsPopupOpen(false);
  };

  const formatDateTime = (dateTime) => {
    if (!dateTime) return "--";
    try {
      const date = new Date(dateTime);
      if (isNaN(date.getTime())) return "--";
      
      const formattedDate = date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });

      const formattedTime = date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "numeric",
        hour12: true,
      });

      return `${formattedDate} ${formattedTime}`;
    } catch (e) {
      return "--";
    }
  };

  const showingCount = allPayouts.length;
  const isFiltered = searchTerm || workshopSlotStatusFilter !== "all" || payoutStatusFilter !== "all";

  return (
    <div>
      <Header />
      {modalUpdate_PassPayoutIsOpen && (
        <Update_PassPayout
          onClose={closeUpdate_passPayout}
          passPayoutId={selectedpassPayoutId}
          passPayoutStatus={selectedpassPayoutStatus}
          instructorPayment={selectedinstructorPayment}
          venueProviderPayment={selectedvenueProviderPayment}
          brandRepPayment={selectedbrandRepPayment}
          instructorPaymentDone={selectedinstructorPaymentDone}
          venueProviderPaymentDone={selectedvenueProviderPaymentDone}
          brandRepPaymentDone={selectedbrandRepPaymentDone}
          remark={selectedremark}
        />
      )}
      {isBankDetailsPopupOpen && (
        <BankDetailsPopup
          isOpen={isBankDetailsPopupOpen}
          onClose={closeBankDetailsPopup}
          bankDetails={selectedBankDetails}
        />
      )}
      
      <main id="main" className="main">
        <div className="pagetitle">
          <h1>Pass PayOut</h1>
        </div>
        <section className="section">
          <div className="card p-2">
            <div className="parent col-md-12 row mt-2">
              <div className="col-md-8 d-flex flex-wrap">
                <form onSubmit={handleSearchSubmit} className="d-flex">
                  <div className="child mr-2 mb-2">
                    <input
                      ref={searchInputRef}
                      onChange={handleSearchInputChange}
                      onKeyPress={handleKeyPress}
                      value={searchQuery}
                      type="text"
                      className="form-control"
                      placeholder="Search all fields..."
                    />
                  </div>
                  <div className="child mr-2 mb-2">
                    <button 
                      type="submit" 
                      className="btn btn-primary"
                      disabled={isFetching}
                    >
                      {isFetching ? 'Searching...' : 'Search'}
                    </button>
                  </div>
                </form>
                <div className="child mr-2 mb-2" style={{ minWidth: "200px" }}>
                  <Select
                    options={workshopSlotStatusOptions}
                    placeholder="Workshop Status"
                    onChange={handleWorkshopSlotStatusChange}
                    value={workshopSlotStatusOptions.find(option => option.value === workshopSlotStatusFilter)}
                    isDisabled={isFetching}
                  />
                </div>
                <div className="child mr-2 mb-2" style={{ minWidth: "200px" }}>
                  <Select
                    options={payoutStatusOptions}
                    placeholder="Payout Status"
                    onChange={handlePayoutStatusChange}
                    value={payoutStatusOptions.find(option => option.value === payoutStatusFilter)}
                    isDisabled={isFetching}
                  />
                </div>
              </div>
            </div>

            <div className="table-responsive" ref={tableContainerRef} style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              {isLoading ? (
                <div className="text-center my-5">
                  <TailSpin color="#0d6efd" width={50} />
                </div>
              ) : allPayouts.length > 0 ? (
                <>
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th scope="col">Sr.No</th>
                        <th scope="col">Workshop Details</th>
                        <th scope="col">Pass Details</th>
                        <th scope="col">Customer Details</th>
                        <th scope="col">Transaction details</th>
                        <th scope="col">Instructor Details</th>
                        <th scope="col">Instructor Commission</th>
                        <th scope="col">Brand Representative Details</th>
                        <th scope="col">Brand Representative Commission</th>
                        <th scope="col">Venue Details</th>
                        <th scope="col">Venue Provider Details</th>
                        <th scope="col">Venue Provider Commission</th>
                        <th scope="col">Workshop Slot Status</th>
                        <th scope="col">Amount Received</th>
                        <th scope="col">Booking Date/Time</th>
                        <th scope="col">Instructor Payment Done</th>
                        <th scope="col">Venue Provider Payment Done</th>
                        <th scope="col">Brand Representative Payment Done</th>
                        <th scope="col">Payout Status</th>
                        <th scope="col">Remark</th>
                        <th scope="col">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allPayouts.map((payout, index) => (
                        <tr key={`${payout.id}-${index}`}>
                          <th scope="row">{index + 1}</th>
                          <td>
                            {payout.pass_schedule?.workshop?.name || "--"}
                            <br />
                            {payout.pass_schedule?.workshop_slot?.date || "--"}
                            <br />
                            {payout.pass_schedule?.workshop_slot?.startTime || "--"} - {payout.pass_schedule?.workshop_slot?.endTime || "--"}
                          </td>
                          <td>
                            {payout.passes?.title || "--"}                  
                            <br />
                            {payout.passes?.duration_type || "--"}
                          </td>
                          <td>
                            {payout.pass_management?.users?.name || "--"}
                            <br />
                            {payout.pass_management?.users?.email || "--"}
                            <br />
                            {payout.pass_management?.users?.mobileNumber || "--"}
                          </td>
                          <td>
                            <b>OrderId:</b> {payout.pass_management?.transaction?.order_id || "--"}
                            <br />
                            <b>PayId:</b> {payout.pass_management?.transaction?.payment_id || "--"}
                          </td>
                          <td>
                            {payout.instructor?.name || "--"}
                            <br />
                            {payout.instructor?.mobileNumber || "--"}
                            <br />
                            {payout.instructor?.email || "--"}
                            <br />
                            <span
                              style={{ cursor: "pointer", color: "blue" }}
                              onClick={() =>
                                openBankDetailsPopup({
                                  bank_name: payout.instructor?.bank_name,
                                  bank_account_number: payout.instructor?.bank_account_number,
                                  bank_ifsc_code: payout.instructor?.bank_ifsc_code,
                                  bank_account_type: payout.instructor?.bank_account_type,
                                })
                              }
                            >
                              <i className="bi bi-bank"></i> View Bank Details
                            </span>
                          </td>
                          <td>{payout.instructor_pay || "0"} ({payout.pass_schedule?.instructor_commission || "0"}%)</td>
                          <td>
                            {payout.brand_representative?.name || "--"}
                            <br />
                            {payout.brand_representative?.code || "--"}
                            <br />
                            {payout.brand_representative?.mobileNumber || "--"}
                            <br />
                            {payout.brand_representative?.email || "--"}
                            <br />
                            <span
                              style={{ cursor: "pointer", color: "blue" }}
                              onClick={() =>
                                openBankDetailsPopup({
                                  bank_name: payout.brand_representative?.bank_name,
                                  bank_account_number: payout.brand_representative?.bank_account_number,
                                  bank_ifsc_code: payout.brand_representative?.bank_ifsc_code,
                                  bank_account_type: payout.brand_representative?.bank_account_type,
                                })
                              }
                            >
                              <i className="bi bi-bank"></i> View Bank Details
                            </span>
                          </td>
                          <td>{payout.brand_representative_pay || "0"} ({payout.pass_management?.br_commission || "0"}%)</td>
                          <td>{payout.venue?.name || "--"}</td>
                          <td>
                            {payout.venue?.venue_owner?.name || "--"}
                            <br />
                            {payout.venue?.venue_owner?.mobileNumber || "--"}
                            <br />
                            {payout.venue?.venue_owner?.email || "--"}
                            <br />
                            <span
                              style={{ cursor: "pointer", color: "blue" }}
                              onClick={() =>
                                openBankDetailsPopup({
                                  bank_name: payout.venue?.venue_owner?.bank_name,
                                  bank_account_number: payout.venue?.venue_owner?.bank_account_number,
                                  bank_ifsc_code: payout.venue?.venue_owner?.bank_ifsc_code,
                                  bank_account_type: payout.venue?.venue_owner?.bank_account_type,
                                })
                              }
                            >
                              <i className="bi bi-bank"></i> View Bank Details
                            </span>
                          </td>
                          <td>{payout.vp_pay || "0"} ({payout.pass_schedule?.vp_commission || "0"}%)</td>
                          <td>{payout.pass_schedule?.workshop_slot?.status || "No Status"}</td>
                          <td>{payout.amount || "0"}</td>
                          <td>{formatDateTime(payout.created_at)}</td>
                          <td>{payout.instructor_pay_done || "0"}</td>
                          <td>{payout.vp_pay_done || "0"}</td>
                          <td>{payout.br_pay_done || "0"}</td>
                          <td>{payout.status || "--"}</td>
                          <td>{payout.remark || "--"}</td>
                          <td>
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() =>
                                openUpdate_passPayout(
                                  payout.id,
                                  payout.status,
                                  payout.instructor_pay,
                                  payout.vp_pay,
                                  payout.brand_representative_pay,
                                  payout.instructor_pay_done,
                                  payout.vp_pay_done,
                                  payout.br_pay_done,
                                  payout.remark
                                )
                              }
                            >
                              <i className="bi bi-pencil"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {isFetching && (
                    <div className="text-center py-3">
                      <TailSpin color="#0d6efd" width={50} />
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-4">
                  <h4>No payout data found</h4>
                  <p>Try adjusting your search or filters</p>
                </div>
              )}
            </div>

            <div className="p-3 bg-dark text-white rounded-bottom">
              Showing {showingCount} of {totalItems} total payouts
              {hasMore && !isFetching && !isFiltered && " - scroll to load more"}
              {hasMore && isFiltered && (
                <button 
                  className="btn btn-sm btn-light ml-2"
                  onClick={() => {
                    const nextPage = page + 1;
                    if (pagination.total_pages && nextPage <= pagination.total_pages) {
                      setPage(nextPage);
                    }
                  }}
                  disabled={isFetching || (pagination.total_pages && page >= pagination.total_pages)}
                >
                  {isFetching ? 'Loading...' : 'Load More'}
                </button>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

export default PassPayout;